import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Carregando from "../componentes/Carregando";
import { CAMPO } from "../componentes/ui";
import { CurriculoFolha, MiniaturaDaFolha } from "../componentes/CurriculoFolha";
import { Segmentos } from "../componentes/FerramentaUi";
import { lerCurriculo, salvarCurriculo } from "../lib/curriculo";
import { mascararTelefone } from "../lib/mascaras";
import {
  CURSO_VAZIO,
  EXPERIENCIA_VAZIA,
  FORMACAO_VAZIA,
  IDIOMA_VAZIO,
  MESES,
  NIVEIS_IDIOMA,
  TEMAS,
  anosDoPeriodo,
  handleDoLinkedin,
  linkedinValido,
  partesDoPeriodo,
  preenchimentoDe,
  textoDoPeriodo,
  type Curriculo,
  type Periodo,
  type Tema,
} from "../lib/curriculoModelo";

// O editor do currículo: formulário à esquerda, folha à direita.
//
// ---------------------------------------------------------------------------
// UMA tela, não duas
// ---------------------------------------------------------------------------
// O app de origem tinha dois editores completos, sempre os DOIS montados: uma
// grade de duas colunas no desktop e um assistente de seis passos no celular.
// Com a galeria de temas aberta havia sete cópias do documento na página ao mesmo
// tempo. Aqui é uma tela responsiva: a folha desce para baixo do formulário
// abaixo de `lg`, e existe uma cópia só.
//
// ---------------------------------------------------------------------------
// O PDF é impressão, não imagem
// ---------------------------------------------------------------------------
// O app de origem gerava o PDF com `html2canvas` + `jsPDF`: capturava um clone
// escondido de 794px e colava fatias JPEG. Duas consequências medidas naquele
// código:
//
//   · o PDF é uma IMAGEM. O texto não é selecionável, e — o que importa de
//     verdade — **sistema de recrutamento não consegue ler**. Um currículo que o
//     ATS não lê é um currículo que não chega ao recrutador;
//   · a nitidez caía com o tamanho: a escala começava em 2 (≈192 DPI) e descia
//     para 1,25 conforme o documento crescia, sem avisar ninguém. Acima de ~12
//     páginas não havia proteção nenhuma.
//
// Aqui a folha é impressa pelo navegador (`window.print()` com `@media print`),
// como o resumo de honorários do IR Proposta já faz. O texto sai vetorial e
// selecionável em qualquer tamanho, o link do LinkedIn sai clicável de verdade,
// e não entram duas dependências novas no bundle.
//
// O preço, e ele é real: o aluno passa pela janela de impressão do navegador e
// precisa escolher "Salvar como PDF". O painel diz isso com letra, junto com as
// duas opções que importam (margens padrão, cabeçalhos desmarcados).

const ESPERA_ANTES_DE_SALVAR = 1200;

export default function CurriculoEditor({ id }: { id: string }) {
  const [c, setC] = useState<Curriculo | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [estado, setEstado] = useState<"limpo" | "sujo" | "salvando" | "salvo">("limpo");
  const [aba, setAba] = useState<"dados" | "experiencia" | "formacao" | "extras" | "modelo">("dados");

  // O que já foi gravado, para o autosave mandar só o que mudou.
  const gravado = useRef<Curriculo | null>(null);
  const relogio = useRef<number | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await lerCurriculo(id);
      if (r === null) {
        setErro("Não encontramos este currículo. Ele pode ter sido excluído.");
        return;
      }
      setC(r);
      gravado.current = r;
    })();
  }, [id]);

  const salvar = useCallback(async (atual: Curriculo) => {
    setEstado("salvando");
    const r = await salvarCurriculo(atual.id, {
      titulo: atual.titulo,
      tema: atual.tema,
      nome: atual.nome,
      email: atual.email,
      telefone: atual.telefone,
      linkedin: atual.linkedin,
      localidade: atual.localidade,
      resumo: atual.resumo,
      habilidades: atual.habilidades,
      informacoes_adicionais: atual.informacoes_adicionais,
      experiencias: atual.experiencias,
      formacoes: atual.formacoes,
      cursos: atual.cursos,
      idiomas: atual.idiomas,
    });
    if (!r.ok) {
      setErro(r.mensagem);
      setEstado("sujo");
      return;
    }
    gravado.current = atual;
    setErro(null);
    setEstado("salvo");
  }, []);

  // Autosave com espera. O app de origem tinha botão "salvar", e um aluno que
  // fechasse a aba perdia tudo o que tinha digitado — o dado é dele e não custa
  // nada guardar. A espera existe para não mandar uma chamada por tecla.
  const mudar = (novo: Curriculo) => {
    setC(novo);
    setEstado("sujo");
    if (relogio.current !== null) window.clearTimeout(relogio.current);
    relogio.current = window.setTimeout(() => void salvar(novo), ESPERA_ANTES_DE_SALVAR);
  };

  // Sair com algo pendente salva antes. `beforeunload` não espera promessa, mas
  // o `setTimeout` pendente é disparado na hora — o que cobre o caso comum de
  // fechar a aba um segundo depois de digitar.
  useEffect(() => {
    const aoSair = () => {
      if (estado === "sujo" && c) void salvar(c);
    };
    window.addEventListener("beforeunload", aoSair);
    return () => {
      window.removeEventListener("beforeunload", aoSair);
      aoSair();
    };
  }, [estado, c, salvar]);

  const preenchimento = useMemo(() => (c ? preenchimentoDe(c) : null), [c]);

  if (erro && c === null) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <Link to="/ferramenta/gerador-de-curriculos" className="text-sm font-semibold text-link">
          ← Meus currículos
        </Link>
        <p className="surface mt-4 p-6 text-center text-sm leading-relaxed text-cinza">{erro}</p>
      </main>
    );
  }
  if (!c || !preenchimento) return <Carregando />;

  const campo = (chave: keyof Curriculo, valor: string) => mudar({ ...c, [chave]: valor });

  return (
    <>
      {/* A moldura NÃO é impressa: `no-imprimir` a remove no @media print, e o
          que sobra na folha A4 é só o documento. */}
      <main className="mx-auto max-w-[100rem] px-4 py-6 sm:px-6">
        <div className="no-imprimir">
          {/* A cabine do editor (redesenho de 26/08): navy, compacta, com a
              trilha de volta, o nome do currículo, as OITO PARTES em barra
              segmentada e o estado do autosave como selo. Antes era um `h1`
              solto e uma linha cinza "6 de 8 partes preenchidas · modelo
              Executivo" — informação certa, sem hierarquia nenhuma.
              Sobre navy só cor de marca: `text-tinta` aqui viraria branco
              sobre branco no escuro (a cicatriz dos cards em escada). */}
          <header className="painel-navy rounded-cf-lg px-5 py-5 sm:px-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <nav aria-label="Trilha" className="text-xs tracking-[0.14em] text-navy-mute uppercase">
                  <Link
                    to="/ferramenta/gerador-de-curriculos"
                    className="underline decoration-navy-mute/40 hover:text-navy-ink"
                  >
                    ← Meus currículos
                  </Link>
                </nav>
                <h1 className="rise mt-1 font-display text-2xl font-bold text-white sm:text-3xl">
                  {c.titulo}
                </h1>
                <div className="rise mt-3 max-w-md" style={{ "--i": 1 } as React.CSSProperties}>
                  <Segmentos total={8} acesos={preenchimento.partesPreenchidas} rotulo="Partes preenchidas" />
                  <p className="mt-1.5 text-xs text-navy-mute tabular-nums">
                    {preenchimento.partesPreenchidas} de 8 partes preenchidas · modelo{" "}
                    {TEMAS.find((t) => t.id === c.tema)?.nome}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <span
                  data-estado={estado}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                    estado === "salvo"
                      ? "bg-sucesso/20 text-sucesso-claro"
                      : estado === "sujo"
                        ? "bg-laranja/20 text-laranja-claro"
                        : "bg-white/10 text-navy-mute"
                  }`}
                >
                  {estado === "salvo" && "Salvo"}
                  {estado === "salvando" && "Salvando…"}
                  {estado === "sujo" && "Alterações não salvas"}
                  {estado === "limpo" && "Tudo salvo"}
                </span>
                <button
                  type="button"
                  onClick={() => window.print()}
                  disabled={!preenchimento.podeBaixar}
                  title={
                    preenchimento.podeBaixar
                      ? "Abrir a janela de impressão para salvar em PDF"
                      : "Preencha o seu nome para poder baixar"
                  }
                  className="pill-cta text-sm disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Baixar em PDF
                </button>
              </div>
            </div>
          </header>

          {/* A instrução vive no MOMENTO do clique, e não só no painel.
              Ela existe lá, em "Como o PDF sai daqui" — mas o painel é a tela
              anterior, e o aluno lê aquilo no caminho de entrada, não quando a
              janela de impressão abre. O `title` do botão não resolve: em toque
              não existe hover, então no celular a instrução simplesmente não
              aparecia no instante em que ela importa.
              Referendado por Victor em 25/08: o passo extra fica, em troca de o
              currículo ser legível por ATS. Decisão firme pede instrução firme. */}
          {preenchimento.podeBaixar && (
            <p className="mt-2 text-xs leading-relaxed text-cinza">
              Na janela de impressão, escolha <strong className="text-tinta">"Salvar como
              PDF"</strong>. Deixe as margens em "Padrão" e desmarque "Cabeçalhos e rodapés" —
              sem isso o navegador imprime a data e o endereço da página no seu currículo.
            </p>
          )}

          {erro && (
            <p className="mt-3 rounded-xl border-l-4 border-erro-tinta bg-erro-fundo px-4 py-3 text-sm text-erro-tinta">
              {erro}
            </p>
          )}
        </div>

        <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,26rem)_1fr]">
          <div className="no-imprimir">
            {/* Cada aba carrega um ponto quando a parte dela já tem conteúdo — o
                aluno vê de longe onde ainda falta. O ponto é um `span` VAZIO
                (`aria-hidden`), então o `textContent` do botão continua sendo só
                o rótulo: é por ele que a prova acha a aba, e é por ele que o
                leitor de tela a anuncia. O estado vai no `title`. */}
            <nav aria-label="Partes do currículo" className="mb-4 flex flex-wrap gap-1.5">
              {(
                [
                  ["dados", "Dados", preenchimento.temIdentificacao && preenchimento.temContato],
                  ["experiencia", "Experiência", preenchimento.experiencias > 0],
                  ["formacao", "Formação", preenchimento.formacoes > 0],
                  [
                    "extras",
                    "Extras",
                    preenchimento.temHabilidades ||
                      preenchimento.idiomas > 0 ||
                      preenchimento.cursos > 0 ||
                      preenchimento.temInformacoes,
                  ],
                  ["modelo", "Modelo", null],
                ] as const
              ).map(([chave, rotulo, preenchida]) => (
                <button
                  key={chave}
                  type="button"
                  onClick={() => setAba(chave)}
                  aria-current={aba === chave ? "page" : undefined}
                  title={
                    preenchida === null ? undefined : preenchida ? `${rotulo}: preenchida` : `${rotulo}: ainda vazia`
                  }
                  className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition ${
                    aba === chave ? "bg-azul text-white" : "border border-borda text-titulo hover:bg-chip"
                  }`}
                >
                  {preenchida !== null && (
                    <span
                      aria-hidden="true"
                      className={`size-1.5 rounded-full ${
                        preenchida ? "bg-sucesso" : aba === chave ? "bg-white/40" : "bg-borda"
                      }`}
                    />
                  )}
                  {rotulo}
                </button>
              ))}
            </nav>

            {aba === "dados" && <AbaDados c={c} campo={campo} mudar={mudar} />}
            {aba === "experiencia" && <AbaExperiencia c={c} mudar={mudar} />}
            {aba === "formacao" && <AbaFormacao c={c} mudar={mudar} />}
            {aba === "extras" && <AbaExtras c={c} campo={campo} mudar={mudar} />}
            {aba === "modelo" && <AbaModelo c={c} mudar={mudar} />}
          </div>

          <div>
            <p className="no-imprimir mb-2 text-xs text-cinza">
              Prévia — é exatamente o que sai no PDF.
            </p>
            <div className="area-de-impressao overflow-x-auto rounded-xl border border-borda bg-white shadow-sm">
              <CurriculoFolha c={c} id="folha-do-curriculo" fluida />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

// ---------------------------------------------------------------------------
// Peças do formulário
// ---------------------------------------------------------------------------

function Bloco({ titulo, children, dica }: { titulo: string; children: React.ReactNode; dica?: string }) {
  const id = `bloco-${titulo.toLowerCase().replace(/\W+/g, "-")}`;
  return (
    // `<section role="group" aria-labelledby>` com `<h3>`, nunca
    // `<fieldset><legend>`: num card arredondado o legend sai flutuando fora da
    // caixa. Cicatriz de 19/08.
    <section role="group" aria-labelledby={id} className="surface mb-4 p-4 sm:p-5">
      <h3 id={id} className="font-display text-sm font-bold text-titulo">
        {titulo}
      </h3>
      {dica && <p className="mt-0.5 mb-3 text-xs leading-relaxed text-cinza">{dica}</p>}
      <div className={dica ? "space-y-3" : "mt-3 space-y-3"}>{children}</div>
    </section>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  dica,
  exemplo,
  maximo,
  erro,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica?: string;
  exemplo?: string;
  maximo?: number;
  erro?: string | null;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-cinza">{rotulo}</span>
      <input
        className={CAMPO}
        value={valor}
        maxLength={maximo}
        placeholder={exemplo}
        onChange={(e) => aoMudar(e.target.value)}
      />
      {erro ? (
        <span className="mt-1 block text-xs text-erro-tinta">{erro}</span>
      ) : (
        dica && <span className="mt-1 block text-xs text-cinza">{dica}</span>
      )}
    </label>
  );
}

function Area({
  rotulo,
  valor,
  aoMudar,
  dica,
  exemplo,
  linhas = 4,
  maximo,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  dica?: string;
  exemplo?: string;
  linhas?: number;
  maximo?: number;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-cinza">{rotulo}</span>
      <textarea
        className="w-full rounded-xl border-[1.5px] border-borda bg-superficie px-3 py-2 text-sm text-tinta transition placeholder:text-cinza/60 focus:border-laranja focus:ring-3 focus:ring-laranja/20 focus:outline-none"
        rows={linhas}
        value={valor}
        maxLength={maximo}
        placeholder={exemplo}
        onChange={(e) => aoMudar(e.target.value)}
      />
      {dica && <span className="mt-1 block text-xs text-cinza">{dica}</span>}
    </label>
  );
}

function AbaDados({
  c,
  campo,
  mudar,
}: {
  c: Curriculo;
  campo: (k: keyof Curriculo, v: string) => void;
  mudar: (n: Curriculo) => void;
}) {
  const [linkedinBruto, setLinkedinBruto] = useState(c.linkedin);
  const [avisoLinkedin, setAvisoLinkedin] = useState<string | null>(null);

  // O aluno cola a URL inteira — é o que 263 dos 268 fizeram no app antigo.
  // Então o campo ACEITA a URL e extrai o handle, em vez de recusar e mandar a
  // pessoa adivinhar o que é "nome de usuário".
  const aplicarLinkedin = (bruto: string) => {
    setLinkedinBruto(bruto);
    if (bruto.trim() === "") {
      setAvisoLinkedin(null);
      campo("linkedin", "");
      return;
    }
    const h = handleDoLinkedin(bruto);
    if (h === null) {
      setAvisoLinkedin(
        "Não reconhecemos um perfil aqui. Cole o endereço do seu LinkedIn (linkedin.com/in/…) ou só o nome de usuário.",
      );
      return;
    }
    setAvisoLinkedin(null);
    campo("linkedin", h);
  };

  return (
    <>
      <Bloco
        titulo="Identificação"
        dica="Estes campos vieram do seu perfil no hub. Mudar aqui não muda o seu perfil — vale só para este currículo."
      >
        <Campo rotulo="Nome completo" valor={c.nome} aoMudar={(v) => campo("nome", v)} maximo={300} />
        <Campo
          rotulo="Cidade e estado"
          valor={c.localidade}
          aoMudar={(v) => campo("localidade", v)}
          exemplo="Campinas, SP"
          maximo={200}
        />
      </Bloco>

      <Bloco titulo="Contato">
        <Campo
          rotulo="E-mail"
          valor={c.email}
          aoMudar={(v) => campo("email", v)}
          exemplo="voce@exemplo.com"
          maximo={200}
        />
        <Campo
          rotulo="Telefone"
          valor={c.telefone}
          aoMudar={(v) => campo("telefone", mascararTelefone(v))}
          exemplo="(11) 90000-0000"
        />
        <Campo
          rotulo="LinkedIn"
          valor={linkedinBruto}
          aoMudar={aplicarLinkedin}
          exemplo="linkedin.com/in/seu-perfil"
          erro={avisoLinkedin}
          dica={
            c.linkedin !== "" && linkedinValido(c.linkedin)
              ? `No currículo, o link vai para linkedin.com/in/${c.linkedin}`
              : "Cole o endereço do seu perfil; guardamos só o nome de usuário."
          }
        />
      </Bloco>

      <Bloco
        titulo="Resumo profissional"
        dica="Três ou quatro linhas sobre quem você é profissionalmente. Se você já escreveu a sua bio no perfil, ela veio para cá."
      >
        <Area
          rotulo="Resumo"
          valor={c.resumo}
          aoMudar={(v) => campo("resumo", v)}
          linhas={5}
          maximo={4000}
          exemplo="Contador com foco em rotinas fiscais do Simples Nacional…"
        />
      </Bloco>

      <Bloco titulo="Nome deste currículo" dica="Só para você se organizar — não aparece no documento.">
        <Campo
          rotulo="Nome"
          valor={c.titulo}
          aoMudar={(v) => mudar({ ...c, titulo: v })}
          maximo={200}
        />
      </Bloco>
    </>
  );
}

function SeletorDePeriodo({
  valor,
  aoMudar,
}: {
  valor: string;
  aoMudar: (v: string) => void;
}) {
  const anos = useMemo(() => anosDoPeriodo(new Date()), []);
  const p = partesDoPeriodo(valor);
  const trocar = (parte: Partial<Periodo>) => aoMudar(textoDoPeriodo({ ...p, ...parte }));
  const sel =
    "h-11 w-full min-w-0 rounded-xl border-[1.5px] border-borda bg-superficie px-2 text-sm text-tinta focus:border-laranja focus:outline-none";

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <span className="mb-1 block text-xs font-semibold text-cinza">Início</span>
        <div className="flex gap-2">
          <select className={sel} value={p.mesInicio} onChange={(e) => trocar({ mesInicio: e.target.value })}>
            <option value="">Mês</option>
            {MESES.map((m) => (
              <option key={m.valor} value={m.valor}>{m.nome}</option>
            ))}
          </select>
          <select className={sel} value={p.anoInicio} onChange={(e) => trocar({ anoInicio: e.target.value })}>
            <option value="">Ano</option>
            {anos.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <span className="mb-1 block text-xs font-semibold text-cinza">Término</span>
        <div className="flex items-center gap-2">
          <select
            className={sel}
            disabled={p.atual}
            value={p.mesFim}
            onChange={(e) => trocar({ mesFim: e.target.value })}
          >
            <option value="">Mês</option>
            {MESES.map((m) => (
              <option key={m.valor} value={m.valor}>{m.nome}</option>
            ))}
          </select>
          <select
            className={sel}
            disabled={p.atual}
            value={p.anoFim}
            onChange={(e) => trocar({ anoFim: e.target.value })}
          >
            <option value="">Ano</option>
            {anos.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <label className="mt-2 flex cursor-pointer items-center gap-1.5 text-xs text-tinta">
          <input
            type="checkbox"
            className="size-4 accent-laranja"
            checked={p.atual}
            onChange={(e) => trocar({ atual: e.target.checked })}
          />
          Estou aqui até hoje
        </label>
      </div>
    </div>
  );
}

function LinhaDaLista({
  titulo,
  aoRemover,
  children,
}: {
  titulo: string;
  aoRemover: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-borda p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-bold tracking-wide text-cinza uppercase">{titulo}</span>
        <button
          type="button"
          onClick={aoRemover}
          title={`Remover ${titulo}`}
          aria-label={`Remover ${titulo}`}
          className="rounded-full px-2 py-1 text-xs font-bold text-cinza transition hover:bg-chip hover:text-erro-tinta"
        >
          Remover
        </button>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function AbaExperiencia({ c, mudar }: { c: Curriculo; mudar: (n: Curriculo) => void }) {
  const trocar = (i: number, chave: string, valor: string) => {
    const lista = c.experiencias.map((e, j) => (i === j ? { ...e, [chave]: valor } : e));
    mudar({ ...c, experiencias: lista });
  };
  return (
    <Bloco
      titulo="Experiência profissional"
      dica="Cada linha da descrição vira um item com marcador no currículo — quebre em linhas curtas, uma entrega por linha."
    >
      {c.experiencias.length === 0 && (
        <p className="text-xs text-cinza">Nenhuma experiência ainda.</p>
      )}
      {c.experiencias.map((e, i) => (
        <LinhaDaLista
          key={i}
          titulo={`Experiência ${i + 1}`}
          aoRemover={() => mudar({ ...c, experiencias: c.experiencias.filter((_, j) => j !== i) })}
        >
          <Campo rotulo="Cargo" valor={e.cargo} aoMudar={(v) => trocar(i, "cargo", v)} maximo={700} />
          <Campo rotulo="Empresa" valor={e.empresa} aoMudar={(v) => trocar(i, "empresa", v)} maximo={700} />
          <Campo rotulo="Cidade" valor={e.local} aoMudar={(v) => trocar(i, "local", v)} maximo={700} />
          <SeletorDePeriodo valor={e.periodo} aoMudar={(v) => trocar(i, "periodo", v)} />
          <Area
            rotulo="O que você fazia"
            valor={e.descricao}
            aoMudar={(v) => trocar(i, "descricao", v)}
            linhas={4}
            maximo={4000}
            exemplo={"Apuração de tributos do Simples Nacional\nConciliação bancária de 12 contas"}
          />
        </LinhaDaLista>
      ))}
      {/* Sem cargo a experiência não aparece no documento — a regra do app de
          origem, preservada. O aviso existe para o aluno não achar que sumiu. */}
      {c.experiencias.some((e) => e.cargo.trim() === "") && (
        <p className="text-xs text-alerta-tinta">
          Experiência sem cargo não aparece no currículo.
        </p>
      )}
      <button
        type="button"
        onClick={() => mudar({ ...c, experiencias: [...c.experiencias, { ...EXPERIENCIA_VAZIA }] })}
        className="rounded-full border border-borda px-4 py-2 text-sm font-semibold text-titulo transition hover:bg-chip"
      >
        + Adicionar experiência
      </button>
    </Bloco>
  );
}

function AbaFormacao({ c, mudar }: { c: Curriculo; mudar: (n: Curriculo) => void }) {
  const trocar = (i: number, chave: string, valor: string) => {
    const lista = c.formacoes.map((f, j) => (i === j ? { ...f, [chave]: valor } : f));
    mudar({ ...c, formacoes: lista });
  };
  return (
    <Bloco titulo="Formação acadêmica">
      {c.formacoes.length === 0 && <p className="text-xs text-cinza">Nenhuma formação ainda.</p>}
      {c.formacoes.map((f, i) => (
        <LinhaDaLista
          key={i}
          titulo={`Formação ${i + 1}`}
          aoRemover={() => mudar({ ...c, formacoes: c.formacoes.filter((_, j) => j !== i) })}
        >
          <Campo
            rotulo="Grau"
            valor={f.titulo}
            aoMudar={(v) => trocar(i, "titulo", v)}
            exemplo="Bacharelado, Pós-graduação, Técnico"
            maximo={700}
          />
          <Campo
            rotulo="Curso"
            valor={f.curso}
            aoMudar={(v) => trocar(i, "curso", v)}
            exemplo="Ciências Contábeis"
            maximo={700}
          />
          <Campo
            rotulo="Instituição"
            valor={f.instituicao}
            aoMudar={(v) => trocar(i, "instituicao", v)}
            maximo={700}
          />
          <Campo rotulo="Cidade" valor={f.local} aoMudar={(v) => trocar(i, "local", v)} maximo={700} />
          <SeletorDePeriodo valor={f.periodo} aoMudar={(v) => trocar(i, "periodo", v)} />
        </LinhaDaLista>
      ))}
      {c.formacoes.some((f) => f.curso.trim() === "") && (
        <p className="text-xs text-alerta-tinta">Formação sem curso não aparece no currículo.</p>
      )}
      <button
        type="button"
        onClick={() => mudar({ ...c, formacoes: [...c.formacoes, { ...FORMACAO_VAZIA }] })}
        className="rounded-full border border-borda px-4 py-2 text-sm font-semibold text-titulo transition hover:bg-chip"
      >
        + Adicionar formação
      </button>
    </Bloco>
  );
}

function AbaExtras({
  c,
  campo,
  mudar,
}: {
  c: Curriculo;
  campo: (k: keyof Curriculo, v: string) => void;
  mudar: (n: Curriculo) => void;
}) {
  const trocarCurso = (i: number, chave: string, valor: string) =>
    mudar({ ...c, cursos: c.cursos.map((x, j) => (i === j ? { ...x, [chave]: valor } : x)) });
  const trocarIdioma = (i: number, chave: string, valor: string) =>
    mudar({ ...c, idiomas: c.idiomas.map((x, j) => (i === j ? { ...x, [chave]: valor } : x)) });

  return (
    <>
      <Bloco
        titulo="Habilidades e competências"
        dica="Uma por linha. Elas viram uma lista no currículo."
      >
        <Area
          rotulo="Habilidades"
          valor={c.habilidades}
          aoMudar={(v) => campo("habilidades", v)}
          linhas={6}
          maximo={5000}
          exemplo={"Excel avançado\nSPED Fiscal e Contribuições\nDomínio Sistemas"}
        />
      </Bloco>

      <Bloco titulo="Cursos complementares">
        {c.cursos.length === 0 && <p className="text-xs text-cinza">Nenhum curso ainda.</p>}
        {c.cursos.map((cu, i) => (
          <LinhaDaLista
            key={i}
            titulo={`Curso ${i + 1}`}
            aoRemover={() => mudar({ ...c, cursos: c.cursos.filter((_, j) => j !== i) })}
          >
            <Campo rotulo="Curso" valor={cu.nome} aoMudar={(v) => trocarCurso(i, "nome", v)} maximo={700} />
            <Campo
              rotulo="Instituição"
              valor={cu.instituicao}
              aoMudar={(v) => trocarCurso(i, "instituicao", v)}
              maximo={700}
            />
          </LinhaDaLista>
        ))}
        <button
          type="button"
          onClick={() => mudar({ ...c, cursos: [...c.cursos, { ...CURSO_VAZIO }] })}
          className="rounded-full border border-borda px-4 py-2 text-sm font-semibold text-titulo transition hover:bg-chip"
        >
          + Adicionar curso
        </button>
      </Bloco>

      <Bloco titulo="Idiomas">
        {c.idiomas.length === 0 && <p className="text-xs text-cinza">Nenhum idioma ainda.</p>}
        {c.idiomas.map((idi, i) => (
          <LinhaDaLista
            key={i}
            titulo={`Idioma ${i + 1}`}
            aoRemover={() => mudar({ ...c, idiomas: c.idiomas.filter((_, j) => j !== i) })}
          >
            <Campo rotulo="Idioma" valor={idi.nome} aoMudar={(v) => trocarIdioma(i, "nome", v)} maximo={700} />
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-cinza">Nível</span>
              <select
                className={CAMPO}
                value={idi.nivel}
                onChange={(e) => trocarIdioma(i, "nivel", e.target.value)}
              >
                <option value="">Selecione</option>
                {NIVEIS_IDIOMA.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </LinhaDaLista>
        ))}
        <button
          type="button"
          onClick={() => mudar({ ...c, idiomas: [...c.idiomas, { ...IDIOMA_VAZIO }] })}
          className="rounded-full border border-borda px-4 py-2 text-sm font-semibold text-titulo transition hover:bg-chip"
        >
          + Adicionar idioma
        </button>
      </Bloco>

      <Bloco
        titulo="Informações adicionais"
        dica="Uma por linha: disponibilidade para mudança, CNH, voluntariado, prêmios."
      >
        <Area
          rotulo="Informações"
          valor={c.informacoes_adicionais}
          aoMudar={(v) => campo("informacoes_adicionais", v)}
          linhas={4}
          maximo={4000}
        />
      </Bloco>
    </>
  );
}

/**
 * A galeria de modelos: os cinco temas desenhados COM OS DADOS DO ALUNO, em
 * miniatura real (redesenho de 26/08).
 *
 * Antes era um ponto colorido e uma frase por tema — o aluno tinha de clicar em
 * cada um para ver a prévia mudar. Agora ele vê os cinco de uma vez, com o
 * próprio nome e a própria experiência dentro, e escolhe pela cara. É o mesmo
 * componente da prévia, escalado; nada de imagem estática.
 *
 * A miniatura é `inert` e fica FORA do botão (irmã dele, não filha): botão com
 * `<a>` dentro é HTML inválido, e a folha tem o link do LinkedIn. O botão é a
 * camada de cima, cobrindo o card inteiro — `data-tema` e `aria-pressed`
 * continuam nele, que é o que a prova cobra.
 */
function AbaModelo({ c, mudar }: { c: Curriculo; mudar: (n: Curriculo) => void }) {
  return (
    <Bloco
      titulo="Modelo do currículo"
      dica="Os cinco, já com os seus dados. A prévia ao lado muda na hora, e todos saem em uma página quando o conteúdo cabe."
    >
      <div className="grid grid-cols-2 gap-3">
        {TEMAS.map((t) => {
          const ativo = t.id === c.tema;
          return (
            <div
              key={t.id}
              className={`relative overflow-hidden rounded-xl border-2 transition ${
                ativo ? "border-laranja shadow-[0_0_0_3px_rgb(255_153_51/0.25)]" : "border-borda hover:border-laranja/50"
              }`}
            >
              <MiniaturaDaFolha c={{ ...c, tema: t.id as Tema }} className="bg-white" />
              <div className="flex items-center justify-between gap-2 border-t border-borda bg-superficie px-3 py-2">
                <span className="text-sm font-bold text-titulo">{t.nome}</span>
                {ativo && <span className="text-xs font-semibold text-laranja-texto">em uso</span>}
              </div>
              <button
                type="button"
                data-tema={t.id}
                aria-pressed={ativo}
                aria-label={`${t.nome} — ${t.descricao}${ativo ? " (em uso)" : ""}`}
                onClick={() => mudar({ ...c, tema: t.id as Tema })}
                className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/50"
              />
            </div>
          );
        })}
      </div>
      <p className="text-xs leading-relaxed text-cinza">
        {TEMAS.find((t) => t.id === c.tema)?.descricao}
      </p>
    </Bloco>
  );
}
